import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import {
  Inbox,
  Mail,
  AlertCircle,
  Loader2,
  Check,
  Trash2,
  Phone,
  Calendar,
  Tag,
  X,
} from "lucide-react";

const KIND_LABEL = {
  contact: "Contact",
  complaint: "Complaint",
};

function StatusPill({ status }) {
  const map = {
    new: ["bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/25", "New"],
    seen: ["bg-[#d49a36]/12 text-[#8a6520] border-[#d49a36]/25", "Seen"],
    resolved: ["bg-[#2d7a4a]/12 text-[#1f5634] border-[#2d7a4a]/25", "Resolved"],
  };
  const [cls, label] = map[status] || map.new;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${cls}`}
      data-testid={`lead-status-${status}`}
    >
      {label}
    </span>
  );
}

function KindPill({ kind }) {
  const isComplaint = kind === "complaint";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${
        isComplaint
          ? "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/25"
          : "bg-[#114232]/8 text-[#114232] border-[#114232]/15"
      }`}
    >
      {isComplaint ? (
        <AlertCircle className="w-3 h-3" />
      ) : (
        <Mail className="w-3 h-3" />
      )}
      {KIND_LABEL[kind] || kind}
    </span>
  );
}

function formatDt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-KE", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function Leads() {
  const [data, setData] = useState({
    items: [],
    total: 0,
    stats: { new: 0, contact_new: 0, complaint_new: 0, total: 0 },
  });
  const [filters, setFilters] = useState({ kind: "all", status: "all", q: "" });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filters.kind !== "all") params.set("kind", filters.kind);
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.q) params.set("q", filters.q);
      const r = await api.get(`/admin/leads?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  async function openLead(id) {
    try {
      const r = await api.get(`/admin/leads/${id}`);
      setSelected(r.data);
      setNote(r.data.note || "");
      // refresh list so the "seen" auto-mark is reflected
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function updateLead(patch) {
    if (!selected) return;
    try {
      const r = await api.patch(`/admin/leads/${selected.id}`, patch);
      setSelected({ ...selected, ...r.data });
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function removeLead() {
    if (!selected) return;
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${selected.id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="p-6 space-y-6" data-testid="leads-page">
      <Toaster richColors position="top-right" />
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#c15337] font-bold mb-1.5">
          Admin · Inbox
        </div>
        <h1 className="font-heading text-3xl font-extrabold text-[#114232] flex items-center gap-3">
          <Inbox className="w-7 h-7" /> Leads
        </h1>
        <p className="text-sm text-[#5c6360] mt-1.5">
          Contact enquiries + complaints submitted through the public marketing
          pages.
        </p>
      </div>

      {/* Stats tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { l: "New", v: data.stats.new, tid: "stat-new" },
          { l: "Contact (new)", v: data.stats.contact_new, tid: "stat-contact-new" },
          { l: "Complaint (new)", v: data.stats.complaint_new, tid: "stat-complaint-new" },
          { l: "Total", v: data.stats.total, tid: "stat-total" },
        ].map(({ l, v, tid }) => (
          <div
            key={l}
            className="rounded-xl bg-white border border-[#114232]/10 p-4"
            data-testid={tid}
          >
            <div className="text-[10px] uppercase tracking-wider text-[#5c6360] font-bold">
              {l}
            </div>
            <div className="font-heading text-2xl font-bold text-[#114232] mt-1">
              {v}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          ["all", "All"],
          ["contact", "Contact"],
          ["complaint", "Complaints"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilters((f) => ({ ...f, kind: k }))}
            data-testid={`filter-kind-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filters.kind === k
                ? "bg-[#114232] text-[#f4f2eb] border-[#114232]"
                : "bg-white text-[#114232] border-[#114232]/15 hover:border-[#114232]/30"
            }`}
          >
            {label}
          </button>
        ))}
        <div className="h-5 w-px bg-[#114232]/10 mx-1 hidden sm:block" />
        {[
          ["all", "Any status"],
          ["new", "New"],
          ["seen", "Seen"],
          ["resolved", "Resolved"],
        ].map(([s, label]) => (
          <button
            key={s}
            onClick={() => setFilters((f) => ({ ...f, status: s }))}
            data-testid={`filter-status-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              filters.status === s
                ? "bg-[#c15337] text-white border-[#c15337]"
                : "bg-white text-[#5c6360] border-[#114232]/10 hover:border-[#114232]/30"
            }`}
          >
            {label}
          </button>
        ))}
        <input
          type="search"
          placeholder="Search name / email / phone…"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          data-testid="lead-search"
          className="ml-auto w-full sm:w-72 px-3 py-1.5 rounded-full border border-[#114232]/15 bg-white text-xs text-[#1b1d1c] focus:outline-none focus:border-[#114232]/40 transition-colors"
        />
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border border-[#114232]/10 overflow-hidden">
        {loading ? (
          <div className="p-12 grid place-items-center text-[#5c6360]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#5c6360]" data-testid="leads-empty">
            No leads match your filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#f4f2eb] text-[10px] uppercase tracking-wider text-[#5c6360]">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Kind</th>
                <th className="text-left px-4 py-3 font-bold">Name</th>
                <th className="text-left px-4 py-3 font-bold hidden sm:table-cell">Contact</th>
                <th className="text-left px-4 py-3 font-bold hidden md:table-cell">Topic</th>
                <th className="text-left px-4 py-3 font-bold">Received</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => openLead(l.id)}
                  data-testid={`lead-row-${l.id}`}
                  className="border-t border-[#114232]/8 hover:bg-[#f4f2eb]/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <KindPill kind={l.kind} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#114232]">{l.name}</div>
                    {l.business && (
                      <div className="text-xs text-[#5c6360] mt-0.5">{l.business}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-xs text-[#5c6360]">
                    <div>{l.phone}</div>
                    {l.email && <div className="mt-0.5">{l.email}</div>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-[#5c6360]">
                    {l.enquiry_type || l.reference || "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5c6360] whitespace-nowrap">
                    {formatDt(l.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={l.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-end"
          onClick={() => setSelected(null)}
          data-testid="lead-drawer-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:w-[480px] bg-[#f4f2eb] h-full overflow-y-auto shadow-2xl"
            data-testid="lead-drawer"
          >
            <div className="sticky top-0 bg-[#f4f2eb]/90 backdrop-blur border-b border-[#114232]/10 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <KindPill kind={selected.kind} />
                <StatusPill status={selected.status} />
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="lead-drawer-close"
                className="w-8 h-8 rounded-full grid place-items-center text-[#5c6360] hover:bg-[#114232]/8 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <div className="font-heading text-2xl font-bold text-[#114232]">
                  {selected.name}
                </div>
                {selected.business && (
                  <div className="text-sm text-[#5c6360] mt-1">{selected.business}</div>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2.5 text-[#1b1d1c]">
                  <Phone className="w-3.5 h-3.5 text-[#5c6360]" />
                  <a
                    href={`https://wa.me/${(selected.phone || "").replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#114232] hover:underline font-semibold"
                  >
                    {selected.phone}
                  </a>
                </div>
                {selected.email && (
                  <div className="flex items-center gap-2.5 text-[#1b1d1c]">
                    <Mail className="w-3.5 h-3.5 text-[#5c6360]" />
                    <a
                      href={`mailto:${selected.email}`}
                      className="text-[#114232] hover:underline"
                    >
                      {selected.email}
                    </a>
                  </div>
                )}
                {(selected.enquiry_type || selected.reference) && (
                  <div className="flex items-center gap-2.5 text-[#1b1d1c]">
                    <Tag className="w-3.5 h-3.5 text-[#5c6360]" />
                    <span className="text-sm">
                      {selected.enquiry_type || `Ref: ${selected.reference}`}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2.5 text-[#1b1d1c]">
                  <Calendar className="w-3.5 h-3.5 text-[#5c6360]" />
                  <span className="text-xs text-[#5c6360]">
                    {formatDt(selected.created_at)}
                  </span>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#5c6360] font-bold mb-2">
                  {selected.kind === "complaint" ? "Description" : "Message"}
                </div>
                <div
                  className="rounded-xl bg-white border border-[#114232]/10 p-4 text-sm text-[#1b1d1c] whitespace-pre-wrap leading-relaxed"
                  data-testid="lead-detail-body"
                >
                  {selected.message || selected.description}
                </div>
              </div>

              {/* Ops note */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#5c6360] font-bold mb-2">
                  Ops note (internal)
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Add internal context, follow-up actions, etc."
                  data-testid="lead-note-input"
                  className="w-full px-3 py-2 rounded-xl border border-[#114232]/15 bg-white text-sm text-[#1b1d1c] focus:outline-none focus:border-[#114232]/40 transition-colors resize-none"
                />
                <button
                  onClick={() => updateLead({ note })}
                  data-testid="lead-save-note"
                  className="mt-2 text-xs font-semibold text-[#c15337] hover:text-[#a8472f]"
                >
                  Save note
                </button>
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                {selected.status !== "resolved" && (
                  <button
                    onClick={() => updateLead({ status: "resolved" })}
                    data-testid="lead-action-resolve"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#2d7a4a] hover:bg-[#266a3f] text-white text-sm font-semibold transition-colors"
                  >
                    <Check className="w-4 h-4" /> Mark resolved
                  </button>
                )}
                {selected.status === "resolved" && (
                  <button
                    onClick={() => updateLead({ status: "seen" })}
                    data-testid="lead-action-reopen"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#d49a36] hover:bg-[#b88526] text-white text-sm font-semibold transition-colors"
                  >
                    Re-open
                  </button>
                )}
                <button
                  onClick={removeLead}
                  data-testid="lead-action-delete"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#c15337]/30 text-[#c15337] hover:bg-[#c15337]/8 text-sm font-semibold transition-colors"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>

              {selected.resolved_at && (
                <div className="text-xs text-[#5c6360] pt-3 border-t border-[#114232]/8">
                  Resolved {formatDt(selected.resolved_at)} by{" "}
                  <span className="text-[#114232] font-semibold">
                    {selected.resolved_by_email || "—"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
